import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Smart email link that:
 *  - Always copies the address to clipboard
 *  - Also attempts to open the device's default mail client (mailto:)
 *  - Doesn't error out if no mail app is configured
 *
 * Use this anywhere we previously had a plain mailto: link.
 */
export default function EmailLink({
  email = "hello@fleurisnoir.com",
  subject = "",
  className = "",
  children,
  showCopyIcon = true,
  testId = "email-link",
}) {
  const [copied, setCopied] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    // Try to copy to clipboard first
    let didCopy = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email);
        didCopy = true;
      } else {
        // Fallback for older browsers / non-secure contexts
        const textarea = document.createElement("textarea");
        textarea.value = email;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        try { document.execCommand("copy"); didCopy = true; } catch { /* ignore */ }
        document.body.removeChild(textarea);
      }
    } catch { /* ignore — we'll still try the mailto */ }

    if (didCopy) {
      setCopied(true);
      toast.success("Email address copied to clipboard", {
        description: email,
      });
      setTimeout(() => setCopied(false), 2500);
    }

    // Also attempt to open the mail client — silently no-op if none configured
    const mailto = `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;
    // Use a temporary anchor so a popup blocker / OS doesn't error visibly
    const tempLink = document.createElement("a");
    tempLink.href = mailto;
    tempLink.target = "_blank";
    tempLink.rel = "noopener noreferrer";
    tempLink.style.display = "none";
    document.body.appendChild(tempLink);
    try { tempLink.click(); } catch { /* ignore */ }
    setTimeout(() => { try { document.body.removeChild(tempLink); } catch { /* ignore */ } }, 100);
  };

  return (
    <a
      href={`mailto:${email}`}
      onClick={handleClick}
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={testId}
    >
      {children || email}
      {showCopyIcon && (
        copied
          ? <Check size={14} className="opacity-70" />
          : <Copy size={12} className="opacity-50" />
      )}
    </a>
  );
}
